import { LitElement, html, css } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import classNames from 'classnames'

export const BUTTON_SIZES = ['large', 'default', 'small', 'auto'] as const
export const BUTTON_TYPES = [
    'primary',
    'secondary',
    'tertiary',
    'negative',
    'text',
    'link',
    'subtle',
    'transparent',
] as const
export interface ButtonAttributes {
    type?: (typeof BUTTON_TYPES)[number]
    size?: (typeof BUTTON_SIZES)[number]
    disabled?: boolean
    'no-text-mobile'?: boolean
    dark?: boolean
    'full-width'?: boolean
    tall?: boolean
    loading?: boolean
    'test-id'?: string
}

@customElement('zaptic-button')
export class ZapticButton extends LitElement {
    static styles = css`
        :host {
            display: contents;
        }

        button {
            display: inline-flex;
            justify-content: center;
            align-items: center;
            border-radius: 4px;
            border: none;
            cursor: pointer;
            font-family: inherit;
            font-weight: var(--font-weight-medium);
            font-size: 16px;
            gap: 0.5rem;
            width: var(--ButtonWidth, auto);
        }

        .icon::slotted(*) {
            display: inline-flex;
        }

        @media (max-width: 768px) {
            button.is-text-hidden-mobile.large {
                width: 48px;
            }
            button.is-text-hidden-mobile.default {
                width: 40px;
            }
            button.is-text-hidden-mobile.small {
                width: 32px;
            }
            .is-text-hidden-mobile .icon::slotted(*) {
                margin-right: 0;
            }
            .is-text-hidden-mobile .label {
                display: none;
            }
        }

        .primary {
            background: var(--purple);
            color: var(--white);
        }
        .primary:hover {
            background: #8d66ff;
        }
        .primary.dark {
            background: var(--darkGrey-200);
        }
        .secondary {
            background: var(--white);
            color: var(--darkGrey);
            border: 1px solid var(--grey5);
        }
        .secondary:hover {
            background: var(--backgroundGrey);
        }
        .tertiary {
            background: var(--grey8);
            color: var(--darkGrey);
        }
        .tertiary:hover {
            background: var(--grey6);
        }
        .negative {
            background: var(--red-500);
            color: var(--white);
        }
        .negative:hover {
            background: var(--red-600);
        }
        .link {
            padding-left: 0 !important;
            padding-right: 0 !important;
            font-weight: var(--font-weight-bold);
            background: 0 0;
            border-color: transparent;
            color: var(--link-blue);
        }
        .link:hover,
        .link:focus {
            filter: brightness(90%);
            text-decoration: underline;
        }
        .link:active {
            filter: brightness(80%);
            text-decoration: underline;
        }
        .link.tall {
            height: 48px;
        }
        .auto {
            line-height: 1;
            padding: 0;
        }

        .large {
            height: 48px;
            padding: 1.2rem 1.6rem;
        }

        .default {
            height: 40px;
            padding: 1.2rem 1.6rem;
        }

        .small {
            height: 32px;
            padding: 0.8rem 1.2rem;
            font-size: 14px;
        }

        .default.has-icon,
        .large.has-icon {
            padding-left: 1.2rem;
        }

        .small.has-icon {
            padding-left: 0.8rem;
        }

        .subtle {
            background-color: var(--white);
            font-weight: var(--font-weight-regular);
            color: var(--grey3);
        }
        .transparent {
            background-color: rgba(0, 0, 0, 0);
            font-weight: var(--font-weight-regular);
            color: var(--grey3);
        }

        .subtle:hover {
            text-decoration: underline;
        }

        .disabled {
            background: var(--grey8);
            color: var(--grey5);
            border: 1px solid var(--grey5);
            cursor: auto;
        }
        .disabled:hover {
            background: var(--grey8);
            color: var(--grey5);
        }

        .text {
            background: var(--white);
        }

        .text:hover,
        .text:focus {
            background: var(--backgroundGrey);
        }

        .label {
            margin-bottom: -3px;
        }
        .full-width {
            width: 100%;
        }

        .loading.large {
            --Spinner-size: 18px;
            --Spinner-thickness: 3px;
        }
        .loading.default {
            --Spinner-size: 12px;
            --Spinner-thickness: 3px;
        }
        .loading.small {
            --Spinner-size: 12px;
            --Spinner-thickness: 2px;
        }
        .loading.link {
            --Spinner-size: 18px;
            --Spinner-thickness: 3px;
        }
        .loading.link .label {
            display: none;
        }
    `

    private hasIcon() {
        const iconSlot = this.querySelector<HTMLSlotElement>('[slot="icon"]')
        return Boolean(iconSlot?.assignedSlot?.assignedNodes().length > 0)
    }

    @property()
    size: ButtonAttributes['size'] = 'default'

    @property()
    type: ButtonAttributes['type'] = 'primary'

    @property({ converter: { fromAttribute: (value) => value === 'true' } })
    disabled: ButtonAttributes['disabled'] = false

    @property({ attribute: 'no-text-mobile', converter: { fromAttribute: (value) => value === 'true' } })
    isTextHiddenMobile: ButtonAttributes['no-text-mobile'] = false

    @property({ attribute: 'full-width', converter: { fromAttribute: (value) => value === 'true' } })
    fullWidth: ButtonAttributes['full-width'] = false

    @property({ converter: { fromAttribute: (value) => value === 'true' } })
    tall: ButtonAttributes['tall'] = false

    @property({ converter: { fromAttribute: (value) => value === 'true' } })
    dark: ButtonAttributes['dark'] = false

    @property({ converter: { fromAttribute: (value) => value === 'true' } })
    loading: ButtonAttributes['loading'] = false

    @property({ attribute: 'test-id' })
    testId?: ButtonAttributes['test-id']

    render() {
        return html`
            <button
                part="button"
                ?disabled=${this.disabled}
                class=${classNames(this.size, this.type, {
                    disabled: this.disabled,
                    'is-text-hidden-mobile': this.isTextHiddenMobile,
                    'full-width': this.fullWidth,
                    tall: this.tall,
                    dark: this.dark,
                    loading: this.loading,
                    'has-icon': this.hasIcon(),
                })}
                data-testid="${this.testId}"
            >
                ${this.loading ? html`<zaptic-spinner></zaptic-spinner>` : html`<slot name="icon" class="icon"></slot>`}
                <slot class="label"></slot>
            </button>
        `
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'zaptic-button': ZapticButton
    }
}
